import { wencesApi } from "@/api/wencesApi";

/**
 * Notificaciones del teléfono (Web Push): respuestas del Agente IA y emails que recibe el usuario.
 * Un solo service worker para todo (public/agent-chat-sw.js, scope /chat): una suscripción por
 * dispositivo. En iPhone solo funciona con la app agregada a la pantalla de inicio (iOS 16.4+).
 */
const SW_URL = "/agent-chat-sw.js";
const SW_SCOPE = "/chat";

function urlBase64ToUint8Array(base64) {
  const padded = (base64 + "=".repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
}

export function isIosDevice() {
  const ua = navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

export function isInstalledApp() {
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    navigator.standalone === true
  );
}

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

/** Avisa al resto de la app (tarjeta, menú, botón del chat) que cambió el estado. */
export const PUSH_STATE_EVENT = "push-state-changed";
function announce(state) {
  window.dispatchEvent(new CustomEvent(PUSH_STATE_EVENT, { detail: { state } }));
}

/**
 * Espera a que ESTA registración tenga su worker activo. No sirve `navigator.serviceWorker.ready`:
 * resuelve solo con un SW cuyo scope cubra la página actual, y el de /chat no cubre el menú ni el
 * resto de la app, así que fuera del chat la promesa no resolvía nunca y el botón quedaba colgado.
 */
function waitUntilActive(reg) {
  if (reg.active) return Promise.resolve(reg);
  const worker = reg.installing || reg.waiting;
  if (!worker) return Promise.resolve(reg);
  return new Promise((resolve, reject) => {
    worker.addEventListener("statechange", () => {
      if (worker.state === "activated") resolve(reg);
      else if (worker.state === "redundant") reject(new Error("Service worker redundant"));
    });
  });
}

/** endpoint + claves (p256dh, auth): el back las usa para cifrar el texto del aviso. */
function saveSubscription(sub) {
  const json = sub.toJSON();
  return wencesApi.post("/api/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
}

/**
 * Estado en este dispositivo: "on" | "off" | "needs-install" (iPhone sin instalar) | "unsupported".
 * Si está activo, reenvía la suscripción: las viejas no tenían las claves guardadas.
 */
export async function getPushState() {
  if (!pushSupported()) return isIosDevice() && !isInstalledApp() ? "needs-install" : "unsupported";
  const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  const sub = reg && (await reg.pushManager.getSubscription());
  const on = !!sub && Notification.permission === "granted";
  if (on) saveSubscription(sub).catch(() => {});
  return on ? "on" : "off";
}

/** @returns {Promise<"on" | "denied" | "unavailable">} */
export async function enablePush() {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return "denied";
  const { data } = await wencesApi.get("/api/push/public-key");
  if (!data || !data.publicKey) return "unavailable";
  const reg = await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
  await waitUntilActive(reg);
  const sub =
    (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    }));
  await saveSubscription(sub);
  announce("on");
  return "on";
}

export async function disablePush() {
  const reg = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    await wencesApi.post("/api/push/unsubscribe", { endpoint: sub.endpoint });
    await sub.unsubscribe();
  }
  announce("off");
}
